import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAllRows';
import { BracketGenerationService } from './BracketGenerationService';

export interface TournamentCategory {
  id: string;
  name: string;
  description: string | null;
  color: string;
  created_at: string;
  updated_at: string | null;
}

export interface UserCategory {
  id: string;
  user_id: string;
  category_id: string;
  assigned_at: string;
  category?: TournamentCategory;
}

export interface TournamentInvitation {
  id: string;
  tournament_id: string;
  user_id: string;
  status: 'pending' | 'accepted' | 'declined';
  invited_by: string | null;
  invited_at: string;
  responded_at: string | null;
  tournament?: {
    title: string;
    start_date: string;
    end_date: string;
    status: string;
  };
  inviter?: {
    username: string;
  };
}

export type BracketBackgroundType = 'shader' | 'image' | 'gradient' | 'none';
export const VALID_MAX_PARTICIPANTS = [4, 8, 16, 32, 64] as const;
export type ValidMaxParticipants = typeof VALID_MAX_PARTICIPANTS[number];

export interface Tournament {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: 'upcoming' | 'active' | 'completed';
  entry_fee: number | null;
  prize_pool_amount: number | null;
  prize_pool_currency: string | null;
  prize_description: string;
  prize_display: string | null;
  title_sponsor: string | null;
  max_participants: number | null;
  category_id: string | null;
  is_private: boolean;
  is_shareable: boolean;
  allow_pre_registration: boolean;
  is_featured: boolean;
  primary_color: string | null;
  secondary_color: string | null;
  logo_url: string | null;
  background_image_url: string | null;
  background_type: BracketBackgroundType | null;
  shader_preset: string | null;
  sponsor_logos: string[] | null;
  sponsor_links: string[] | null;
  rules: string[] | null;
  registration_locked: boolean;
  locked_participant_count: number | null;
  locked_at: string | null;
  registration_close_date: string | null;
  round_duration_minutes: number;
  is_simulated: boolean;
  created_at: string;
  updated_at: string | null;
  category?: TournamentCategory;
  songs?: Array<{
    id: string;
    song_id: string;
  }>;
}

export interface CreateTournamentData {
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  registration_close_date?: string;
  entry_fee?: number;
  prize_pool_amount?: number;
  prize_pool_currency?: string;
  prize_description: string;
  prize_display?: string;
  title_sponsor?: string;
  max_participants?: ValidMaxParticipants;
  category_id?: string;
  is_private: boolean;
  is_shareable: boolean;
  allow_pre_registration: boolean;
  is_featured: boolean;
  is_simulated?: boolean;
  primary_color?: string;
  secondary_color?: string;
  logo_url?: string;
  background_image_url?: string;
  background_type?: BracketBackgroundType;
  shader_preset?: string;
  sponsor_logos?: string[];
  sponsor_links?: string[];
  rules?: string[];
  round_duration_minutes?: number;
  song_ids: string[];
}

export class TournamentService {
  async getAllCategories(): Promise<TournamentCategory[]> {
    const { data, error } = await supabase
      .from('tournament_categories')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  }

  async createCategory(name: string, description?: string, color?: string): Promise<TournamentCategory> {
    const { data, error } = await supabase
      .from('tournament_categories')
      .insert({ name, description, color })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateCategory(id: string, updates: Partial<TournamentCategory>): Promise<TournamentCategory> {
    const { data, error } = await supabase
      .from('tournament_categories')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async deleteCategory(id: string): Promise<void> {
    const { error } = await supabase
      .from('tournament_categories')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async getUserCategories(userId: string): Promise<UserCategory[]> {
    const { data, error } = await supabase
      .from('user_categories')
      .select(`
        *,
        category:tournament_categories(*)
      `)
      .eq('user_id', userId);

    if (error) throw error;
    return data || [];
  }

  async assignCategoryToUser(userId: string, categoryId: string): Promise<UserCategory> {
    const { data, error } = await supabase
      .from('user_categories')
      .insert({ user_id: userId, category_id: categoryId })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async removeCategoryFromUser(userId: string, categoryId: string): Promise<void> {
    const { error } = await supabase
      .from('user_categories')
      .delete()
      .eq('user_id', userId)
      .eq('category_id', categoryId);

    if (error) throw error;
  }

  async getAllTournaments(): Promise<Tournament[]> {
    return fetchAllRows<Tournament>(() =>
      supabase
        .from('tournaments')
        .select(`
          *,
          category:tournament_categories(*),
          songs:tournament_songs(id, song_id, song:songs(id, title, artist))
        `)
        .order('start_date', { ascending: false })
    );
  }

  async getTournamentById(id: string): Promise<Tournament> {
    const { data, error } = await supabase
      .from('tournaments')
      .select(`
        *,
        category:tournament_categories(*),
        songs:tournament_songs(id, song_id, song:songs(id, title, artist))
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  }

  async createTournament(tournamentData: CreateTournamentData): Promise<Tournament> {
    const { song_ids, ...tournamentFields } = tournamentData;

    const { data: tournament, error: tournamentError } = await supabase
      .from('tournaments')
      .insert(tournamentFields)
      .select()
      .single();

    if (tournamentError) throw tournamentError;

    if (song_ids && song_ids.length > 0) {
      const tournamentSongs = song_ids.map(song_id => ({
        tournament_id: tournament.id,
        song_id
      }));

      const { error: songsError } = await supabase
        .from('tournament_songs')
        .insert(tournamentSongs);

      if (songsError) throw songsError;
    }

    return await this.getTournamentById(tournament.id);
  }

  async updateTournament(id: string, updates: Partial<CreateTournamentData>): Promise<Tournament> {
    const { song_ids, ...tournamentFields } = updates;

    const { error: tournamentError } = await supabase
      .from('tournaments')
      .update(tournamentFields)
      .eq('id', id);

    if (tournamentError) throw tournamentError;

    if (song_ids !== undefined) {
      await supabase
        .from('tournament_songs')
        .delete()
        .eq('tournament_id', id);

      if (song_ids.length > 0) {
        const tournamentSongs = song_ids.map(song_id => ({
          tournament_id: id,
          song_id
        }));

        const { error: songsError } = await supabase
          .from('tournament_songs')
          .insert(tournamentSongs);

        if (songsError) throw songsError;
      }
    }

    return await this.getTournamentById(id);
  }

  async deleteTournament(id: string): Promise<void> {
    const { error } = await supabase
      .from('tournaments')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async sendInvitations(tournamentId: string, userIds: string[], invitedBy: string): Promise<void> {
    const invitations = userIds.map(user_id => ({
      tournament_id: tournamentId,
      user_id,
      invited_by: invitedBy,
      status: 'pending' as const
    }));

    const { error } = await supabase
      .from('tournament_invitations')
      .insert(invitations);

    if (error) throw error;
  }

  async getUserInvitations(userId: string): Promise<TournamentInvitation[]> {
    const data = await fetchAllRows<TournamentInvitation>(() =>
      supabase
        .from('tournament_invitations')
        .select(`
          *,
          tournament:tournaments(title, start_date, end_date, status),
          inviter:profiles!tournament_invitations_invited_by_fkey(username)
        `)
        .eq('user_id', userId)
        .order('invited_at', { ascending: false })
    );

    const now = new Date();
    return data.filter(inv => {
      if (!inv.tournament) return false;
      return new Date((inv.tournament as any).end_date) >= now;
    });
  }

  async getTournamentInvitations(tournamentId: string): Promise<TournamentInvitation[]> {
    return fetchAllRows<TournamentInvitation>(() =>
      supabase
        .from('tournament_invitations')
        .select(`
          *,
          profile:profiles!tournament_invitations_user_id_fkey(username, full_name, avatar_url)
        `)
        .eq('tournament_id', tournamentId)
        .order('invited_at', { ascending: false })
    );
  }

  async respondToInvitation(invitationId: string, status: 'accepted' | 'declined'): Promise<void> {
    const { error } = await supabase
      .from('tournament_invitations')
      .update({
        status,
        responded_at: new Date().toISOString()
      })
      .eq('id', invitationId);

    if (error) throw error;
  }

  async acceptInvitationAndJoin(invitationId: string, tournamentId: string, userId: string): Promise<void> {
    await this.respondToInvitation(invitationId, 'accepted');

    const { data: existingEntry } = await supabase
      .from('tournament_entries')
      .select('id')
      .eq('tournament_id', tournamentId)
      .eq('user_id', userId)
      .maybeSingle();

    if (!existingEntry) {
      const { error: entryError } = await supabase
        .from('tournament_entries')
        .insert({
          tournament_id: tournamentId,
          user_id: userId,
          status: 'registered'
        });

      if (entryError) throw entryError;

      await BracketGenerationService.autoRegenerateBracket(tournamentId);
    }
  }

  async removeAcceptedParticipant(invitationId: string, tournamentId: string, userId: string): Promise<void> {
    const { error: entryError } = await supabase
      .from('tournament_entries')
      .delete()
      .eq('tournament_id', tournamentId)
      .eq('user_id', userId);

    if (entryError) throw entryError;

    const { error: invError } = await supabase
      .from('tournament_invitations')
      .delete()
      .eq('id', invitationId);

    if (invError) throw invError;
  }

  async hasBracketBeenGenerated(tournamentId: string): Promise<boolean> {
    const { data } = await supabase
      .from('bracket_progression')
      .select('id')
      .eq('tournament_id', tournamentId)
      .maybeSingle();

    return !!data;
  }

  async canUserAccessTournament(tournamentId: string, userId: string): Promise<boolean> {
    const tournament = await this.getTournamentById(tournamentId);

    if (!tournament.is_private) {
      return true;
    }

    const { data, error } = await supabase
      .from('tournament_invitations')
      .select('id, status')
      .eq('tournament_id', tournamentId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;

    return data !== null && data.status === 'accepted';
  }

  async canUserJoinTournament(tournamentId: string, userId: string): Promise<{ canJoin: boolean; reason?: string }> {
    const tournament = await this.getTournamentById(tournamentId);

    if (tournament.status !== 'active') {
      return { canJoin: false, reason: 'Tournament is not active' };
    }

    if (tournament.is_private) {
      const hasAcceptedInvitation = await this.canUserAccessTournament(tournamentId, userId);
      if (!hasAcceptedInvitation) {
        return { canJoin: false, reason: 'You need an accepted invitation to join this private tournament' };
      }
    }

    if (tournament.category_id) {
      const userCategories = await this.getUserCategories(userId);
      const hasCategory = userCategories.some(uc => uc.category_id === tournament.category_id);
      if (!hasCategory) {
        return { canJoin: false, reason: 'You do not belong to the required category for this tournament' };
      }
    }

    if (tournament.max_participants) {
      const { count, error } = await supabase
        .from('tournament_entries')
        .select('*', { count: 'exact', head: true })
        .eq('tournament_id', tournamentId);

      if (error) throw error;

      if (count && count >= tournament.max_participants) {
        return { canJoin: false, reason: 'Tournament is full' };
      }
    }

    return { canJoin: true };
  }

  async getTournamentWithEntries(tournamentId: string): Promise<any> {
    const { data, error } = await supabase
      .from('tournaments')
      .select('*, tournament_entries(*)')
      .eq('id', tournamentId)
      .maybeSingle();

    if (error) throw new Error('Failed to load tournament');
    return data;
  }

  async getAllProfiles(): Promise<Array<{ id: string; username: string; full_name: string | null }>> {
    return fetchAllRows<{ id: string; username: string; full_name: string | null }>(() =>
      supabase
        .from('profiles')
        .select('id, username, full_name')
        .order('username')
    );
  }

  async setRound1Deadlines(tournamentId: string): Promise<void> {
    const { error } = await supabase.rpc('set_round1_deadlines', {
      tournament_id_param: tournamentId
    });
    if (error) throw error;
  }

  async getPendingInvitationCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('tournament_invitations')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('status', 'pending');

    if (error) throw error;
    return count || 0;
  }

  async getUserInvitationForTournament(tournamentId: string, userId: string): Promise<TournamentInvitation | null> {
    const { data, error } = await supabase
      .from('tournament_invitations')
      .select(`
        *,
        tournament:tournaments(title, start_date, end_date),
        inviter:profiles!tournament_invitations_invited_by_fkey(username)
      `)
      .eq('tournament_id', tournamentId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  async duplicateTournament(sourceId: string, overrides?: { title?: string; start_date?: string; end_date?: string }): Promise<Tournament> {
    const source = await this.getTournamentById(sourceId);

    const newData: CreateTournamentData = {
      title: overrides?.title || `Copy of ${source.title}`,
      description: source.description,
      start_date: overrides?.start_date || source.start_date,
      end_date: overrides?.end_date || source.end_date,
      registration_close_date: source.registration_close_date || undefined,
      entry_fee: source.entry_fee || undefined,
      prize_pool_amount: source.prize_pool_amount || undefined,
      prize_pool_currency: source.prize_pool_currency || undefined,
      prize_description: source.prize_description,
      prize_display: source.prize_display || undefined,
      title_sponsor: source.title_sponsor || undefined,
      max_participants: (source.max_participants as ValidMaxParticipants) || undefined,
      category_id: source.category_id || undefined,
      is_private: source.is_private,
      is_shareable: source.is_shareable,
      allow_pre_registration: source.allow_pre_registration,
      is_featured: false,
      is_simulated: false,
      primary_color: source.primary_color || undefined,
      secondary_color: source.secondary_color || undefined,
      logo_url: source.logo_url || undefined,
      background_image_url: source.background_image_url || undefined,
      background_type: source.background_type || undefined,
      shader_preset: source.shader_preset || undefined,
      sponsor_logos: source.sponsor_logos || undefined,
      sponsor_links: source.sponsor_links || undefined,
      rules: source.rules || undefined,
      round_duration_minutes: source.round_duration_minutes || undefined,
      song_ids: source.songs?.map((s: any) => s.song_id) || [],
    };

    return this.createTournament(newData);
  }
}

export const tournamentService = new TournamentService();

export interface TournamentSongHistoryItem {
  song_id: string;
  round_number: number;
}

// Covers recordings from every platform (web and mobile) for the signed-in user.
export async function getMyTournamentSongHistory(tournamentId: string): Promise<TournamentSongHistoryItem[]> {
  const { data, error } = await supabase.rpc('get_my_tournament_song_history', { p_tournament_id: tournamentId });
  if (error) throw error;
  return (data ?? []) as TournamentSongHistoryItem[];
}

export async function getUserTournamentRecordingLock(
  tournamentId: string,
  round: number | null,
  songId: string
): Promise<{ recordedThisRound: boolean; songUsed: boolean }> {
  const rows = await getMyTournamentSongHistory(tournamentId);
  return {
    recordedThisRound: round !== null && rows.some(r => r.round_number === round),
    songUsed: rows.some(r => r.song_id === songId),
  };
}
